import React, { useCallback, useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Link,
    Paper,
    Skeleton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography
} from '@mui/material';
import { useAuth } from '../../context/AuthContext';
import ConfirmDialog from '../coding/ConfirmDialog';
import { deleteRun, exportRun, listRuns, saveFile, sha256Of } from '../../services/runService';
import { Card, RunStatusChip, dateTime, fmt, shortDate } from './parts';

const slug = (name) => name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
const stamp = (date) => date.toISOString().slice(0, 16).replace('T', '_').replace(':', '');

const progressLine = (run) => `${fmt(run.progress?.blindLocked)} of ${fmt(run.progress?.blindTotal)} blind codes · ${fmt(run.progress?.final)} final`;

// /runs, the researcher's landing page (wf-run-import-1, wf-run-export 1, 1b, 1c, 2; YES-260). Export is
// one action per row, at any time; a run in setup reopens its wizard. Counts only, no transcript text.
const RunsList = () => {
    const { can } = useAuth();
    const [runs, setRuns] = useState(null);
    const [failed, setFailed] = useState(false);
    const [exporting, setExporting] = useState(null);
    const [exported, setExported] = useState(null);
    const [exportError, setExportError] = useState(null);
    const [deleting, setDeleting] = useState(null);
    const [busy, setBusy] = useState(false);
    const [deleteError, setDeleteError] = useState(null);

    const load = useCallback(async () => {
        setFailed(false);
        try {
            setRuns(await listRuns());
        } catch (err) {
            setFailed(true);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const exportOne = async (run) => {
        setExporting(run.id);
        setExportError(null);
        try {
            const now = new Date();
            const { blob, fileName } = await exportRun(run.id);
            const name = fileName || `${slug(run.name)}_${stamp(now)}.zip`;
            saveFile(blob, name);
            const sha = await sha256Of(blob);
            setExported({ name, at: now, run, sha });
            load();
        } catch (err) {
            setExportError(`Couldn’t build the export of ${run.name}. Nothing was written. Try again.`);
        } finally {
            setExporting(null);
        }
    };

    const remove = async () => {
        setBusy(true);
        setDeleteError(null);
        try {
            await deleteRun(deleting.id);
            setDeleting(null);
            load();
        } catch (err) {
            setDeleteError(err.response?.status === 409
                ? 'This run has started or has coding work, so it can’t be deleted. Nothing was deleted.'
                : 'Couldn’t delete the run. Try again.');
        } finally {
            setBusy(false);
        }
    };

    return (
        <Box sx={{ maxWidth: 1100, mx: 'auto' }}>
            <Box sx={{ display: 'flex', alignItems: { xs: 'flex-start', md: 'flex-end' }, justifyContent: 'space-between', gap: 2, flexDirection: { xs: 'column', md: 'row' }, mt: { xs: 1, sm: 2 }, mb: 3 }}>
                <Box>
                    <Typography variant="h2" component="h1">Runs</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>Adjudication runs you manage.</Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'flex-start' }}>
                    {can('lesson.research') && <Button variant="text" size="large" component={RouterLink} to="/runs/agreement">Agreement</Button>}
                    <Button variant="outlined" size="large" component={RouterLink} to="/runs/import">Import a bundle</Button>
                    {can('lesson.research') && <Button variant="contained" size="large" component={RouterLink} to="/runs/new">New run from lessons</Button>}
                </Box>
            </Box>

            {exported && (
                <Alert severity="success" onClose={() => setExported(null)} sx={{ mb: 2 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>Downloaded {exported.name}</Typography>
                    <Typography variant="body2">
                        As of {dateTime(exported.at.toISOString())} · {fmt(exported.run.progress?.blindLocked)} blind codes · {fmt(exported.run.progress?.final)} final labels
                        {exported.sha ? ` · sha256 ${exported.sha.slice(0, 4)}…${exported.sha.slice(-4)}` : ''} · slots only, no transcript text: share through the approved storage, never by email or Slack.
                    </Typography>
                </Alert>
            )}
            {exportError && <Alert severity="error" onClose={() => setExportError(null)} sx={{ mb: 2 }}>{exportError}</Alert>}

            {failed && (
                <Alert severity="error" action={<Button color="inherit" size="small" onClick={load}>Try again</Button>}>Couldn’t load the runs.</Alert>
            )}

            {!failed && runs && runs.length === 0 && (
                <Paper sx={{ p: 3 }}>
                    <Typography variant="h5" component="h2">No runs yet</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>Create a run in one of two ways.</Typography>
                    <Box component="ul" sx={{ mt: 1.5, mb: 0, pl: 2.5, display: 'grid', gap: 0.75, '& li': { typography: 'body2' } }}>
                        <li><strong>Import a bundle</strong>: Study 1’s bundle, or a training run’s mock bundle, with its lessons, original labels and flagged items.</li>
                        <li><strong>New run from lessons</strong>: pick consented, machine-labelled lessons in ADAS and assign their annotators and adjudicators; ADAS computes the flags.</li>
                    </Box>
                </Paper>
            )}

            {!failed && (!runs || runs.length > 0) && (
                <Card title="Runs">
                    <TableContainer sx={{ overflowX: 'auto' }}>
                        <Table size="small" aria-label="Runs" sx={{ minWidth: 760 }}>
                            <TableHead>
                                <TableRow>
                                    {['Run', 'Kind', 'Status', 'Items', 'Progress', 'Last export', ''].map((h, i) => (
                                        <TableCell key={i} align={i === 6 ? 'right' : 'left'}>{h || <Box component="span" sx={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)' }}>Actions</Box>}</TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {!runs && [0, 1].map((i) => <TableRow key={i}>{[0, 1, 2, 3, 4, 5, 6].map((j) => <TableCell key={j}><Skeleton /></TableCell>)}</TableRow>)}
                                {runs && runs.map((run) => {
                                    const setup = run.status === 'setup';
                                    return (
                                        <TableRow key={run.id} hover>
                                            <TableCell>
                                                <Link component={RouterLink} to={setup ? `/runs/${encodeURIComponent(run.id)}/setup` : `/runs/${encodeURIComponent(run.id)}`} underline="hover" sx={{ fontWeight: 700 }}>
                                                    {run.name}
                                                </Link>
                                            </TableCell>
                                            <TableCell>{run.kind}</TableCell>
                                            <TableCell><RunStatusChip status={run.status} /></TableCell>
                                            <TableCell>{fmt(run.items)}</TableCell>
                                            <TableCell sx={{ whiteSpace: 'nowrap' }}>{setup ? '—' : progressLine(run)}</TableCell>
                                            <TableCell sx={{ color: run.lastExportAt ? 'text.primary' : 'text.secondary' }}>{shortDate(run.lastExportAt) || 'none yet'}</TableCell>
                                            <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                                                {setup ? (
                                                    <>
                                                        <Button size="small" variant="outlined" component={RouterLink} to={`/runs/${encodeURIComponent(run.id)}/setup`}>Continue setup</Button>
                                                        {can('data.purge') && (
                                                            <Button size="small" color="error" onClick={() => { setDeleteError(null); setDeleting(run); }} sx={{ ml: 1 }}>Delete</Button>
                                                        )}
                                                    </>
                                                ) : (
                                                    <Button
                                                        size="small"
                                                        variant="outlined"
                                                        onClick={() => exportOne(run)}
                                                        disabled={Boolean(exporting)}
                                                        startIcon={exporting === run.id ? <CircularProgress size={14} color="inherit" /> : null}
                                                    >
                                                        Export
                                                    </Button>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <Typography variant="caption" component="p" color="text.secondary" sx={{ mt: 1.5 }}>
                        Open a run to see its Summary. A run still in setup shows Continue setup in place of Export.
                    </Typography>
                </Card>
            )}

            <ConfirmDialog
                open={Boolean(deleting)}
                title={deleting ? `Delete ${deleting.name}?` : ''}
                confirmLabel="Delete run"
                cancelLabel="Keep the run"
                destructive
                busy={busy}
                error={deleteError}
                onConfirm={remove}
                onClose={() => setDeleting(null)}
            >
                {deleting && (
                    <Typography variant="body2">
                        The run is deleted with everything its import wrote: its bundle, {fmt(deleting.lessons)} lessons, {fmt(deleting.utterances)} utterances, their original labels, {fmt(deleting.items)} items, their flags and its slots. The same bundle can then be imported again. This can’t be undone.
                    </Typography>
                )}
            </ConfirmDialog>
        </Box>
    );
};

export default RunsList;
